import React, { useState, useEffect } from "react";
import { useApp } from "@/context/AppContext";
import client, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loading, StatusBadge, fmtMAD, fileSrc } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { LogOut, Bell, KeyRound, User, CalendarDays, Swords, ClipboardCheck, Wallet, FileText, CheckCircle2, XCircle } from "lucide-react";

const SEL = {
  selected: { label: "Selected", cls: "bg-emerald-500/15 text-emerald-700" },
  not_selected: { label: "Not Selected", cls: "bg-rose-500/15 text-rose-600" },
  pending: { label: "Pending", cls: "bg-amber-500/15 text-amber-600" },
  unavailable: { label: "Unavailable", cls: "bg-slate-500/15 text-slate-600" },
};

export default function ParentDashboard() {
  const { user, logout } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);
  const [brand, setBrand] = useState({});
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });

  const load = () => { setLoading(true); client.get("/portal/parent").then((r) => { setData(r.data); }).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false)); };
  useEffect(() => { load(); client.get("/settings/public").then((r) => setBrand(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (data?.parent?.must_change_password) setPwOpen(true); }, [data]);

  const changePw = async () => {
    if (pw.new_password !== pw.confirm) return toast.error("Passwords do not match");
    try { await client.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password }); toast.success("Password changed"); setPwOpen(false); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const markRead = async (nid) => { try { await client.post(`/portal/parent/notifications/${nid}/read`); load(); } catch (e) {} };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loading /></div>;
  const players = data?.players || [];
  const notifs = data?.notifications || [];
  const unread = notifs.filter((n) => !n.read).length;
  const cur = players[active];
  const logo = brand.logo_url || "/logo.png";
  const name = brand.academy_name || data?.academy_name || "Touzani Elite Academy";

  return (
    <div className="min-h-screen bg-muted/30">
      <header className="bg-[#0F172A] text-white">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3"><img src={logo} alt={name} className="h-9 w-9 object-contain" /><div><div className="font-extrabold font-heading leading-none">{name}</div><div className="text-xs text-slate-300">Parent Portal</div></div></div>
          <div className="flex items-center gap-2">
            <span className="text-sm hidden sm:block">Welcome, <b>{data?.parent?.name}</b></span>
            <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" onClick={() => setPwOpen(true)} data-testid="parent-changepw-btn"><KeyRound size={16} /></Button>
            <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" onClick={logout} data-testid="parent-logout"><LogOut size={16} className="me-1" /> Logout</Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-extrabold font-heading mb-1">Welcome, {data?.parent?.name} 👋</h1>
        <p className="text-muted-foreground mb-6">Follow your {players.length > 1 ? "children's" : "child's"} progress at the academy.</p>

        {players.length === 0 ? (
          <Card className="p-12 text-center text-muted-foreground">No players are linked to your account yet. Please contact the academy.</Card>
        ) : (
          <>
            <div className="flex gap-2 flex-wrap mb-4" data-testid="player-switcher">
              {players.map((pp, i) => (
                <button key={pp.player.id} onClick={() => setActive(i)} data-testid={`switch-player-${i}`}
                  className={`px-4 py-2 rounded-xl border text-sm font-medium transition-colors ${i === active ? "bg-emerald-600 text-white border-emerald-600" : "bg-background border-border hover:bg-muted"}`}>
                  {pp.player.full_name} <span className={i === active ? "text-emerald-100" : "text-muted-foreground"}>· {pp.player.category}</span>
                </button>
              ))}
            </div>

            {cur && (
              <>
                <Card className="p-5 mb-4 flex flex-wrap items-center gap-4">
                  {cur.player.photo_url ? <img src={fileSrc(cur.player.photo_url)} alt="" className="h-16 w-16 rounded-xl object-cover" /> : <div className="h-16 w-16 rounded-xl bg-emerald-500 text-white flex items-center justify-center text-2xl font-black">{cur.player.full_name?.[0]}</div>}
                  <div className="flex-1 min-w-0">
                    <div className="text-lg font-bold">{cur.player.full_name}</div>
                    <div className="flex gap-2 flex-wrap mt-1 text-sm text-muted-foreground">
                      <StatusBadge status={cur.player.category} /><span>{cur.team?.name || "No team"}</span><span>·</span><StatusBadge status={cur.player.status} />
                    </div>
                  </div>
                  <div className="text-center"><div className="text-2xl font-black text-emerald-600">{cur.attendance_pct ?? "—"}{cur.attendance_pct != null && "%"}</div><div className="text-xs text-muted-foreground">Attendance</div></div>
                </Card>

                <Tabs defaultValue="payments">
                  <TabsList className="flex-wrap h-auto">
                    <TabsTrigger value="payments" data-testid="ptab-payments"><Wallet size={14} className="me-1" /> Payments</TabsTrigger>
                    <TabsTrigger value="matches" data-testid="ptab-matches"><Swords size={14} className="me-1" /> Match Selection</TabsTrigger>
                    <TabsTrigger value="training" data-testid="ptab-training"><CalendarDays size={14} className="me-1" /> Training</TabsTrigger>
                    <TabsTrigger value="documents" data-testid="ptab-documents"><FileText size={14} className="me-1" /> Documents</TabsTrigger>
                    <TabsTrigger value="profile" data-testid="ptab-profile"><User size={14} className="me-1" /> Profile</TabsTrigger>
                  </TabsList>

                  <TabsContent value="payments" className="mt-4">
                    <Card className="p-5">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-bold">Payment History</h3>
                        <div className="text-end"><div className="text-xs text-muted-foreground">Outstanding</div><div className={`font-bold ${cur.outstanding > 0 ? "text-rose-600" : "text-emerald-600"}`}>{fmtMAD(cur.outstanding)}</div></div>
                      </div>
                      {cur.payments.length === 0 ? <p className="text-sm text-muted-foreground">No payment records yet.</p> : (
                        <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="text-start text-muted-foreground border-b border-border"><th className="py-2 text-start">Month</th><th className="text-start">Type</th><th className="text-start">Amount</th><th className="text-start">Status</th><th className="text-start">Date</th></tr></thead>
                          <tbody>{cur.payments.map((p) => <tr key={p.id} className="border-b border-border last:border-0"><td className="py-2">{p.month || "—"}</td><td className="capitalize">{p.type}</td><td>{fmtMAD(p.amount)}</td><td><StatusBadge status={p.status} /></td><td className="text-muted-foreground">{p.date}</td></tr>)}</tbody></table></div>
                      )}
                      <p className="text-xs text-muted-foreground mt-3">Payments are managed by the academy. For questions, contact the office.</p>
                    </Card>
                  </TabsContent>

                  <TabsContent value="matches" className="mt-4">
                    <Card className="p-5">
                      <h3 className="font-bold mb-4">Match Selection</h3>
                      {cur.matches.length === 0 ? <p className="text-sm text-muted-foreground">No matches scheduled.</p> : (
                        <div className="space-y-3">{cur.matches.map((m) => {
                          const st = SEL[m.my_selection] || { label: "No status yet", cls: "bg-muted text-muted-foreground" };
                          return (
                            <div key={m.id} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-border" data-testid={`parent-match-${m.id}`}>
                              <div><div className="font-semibold">{name} <span className="text-muted-foreground font-normal">vs</span> {m.opponent}</div><div className="text-xs text-muted-foreground">{m.date} {m.time} {m.stadium && `· ${m.stadium}`}</div></div>
                              <span className={`text-xs font-semibold px-3 py-1 rounded-full ${st.cls}`}>{st.label}</span>
                            </div>
                          );
                        })}</div>
                      )}
                    </Card>
                  </TabsContent>

                  <TabsContent value="training" className="mt-4">
                    <Card className="p-5"><h3 className="font-bold mb-4">Upcoming Training</h3>
                      {cur.trainings.length === 0 ? <p className="text-sm text-muted-foreground">No training scheduled.</p> :
                        cur.trainings.map((tr) => <div key={tr.id} className="flex justify-between py-2 border-b border-border last:border-0 text-sm"><span>{tr.title || "Training"} {tr.field && `· ${tr.field}`}</span><span className="text-muted-foreground">{tr.date} {tr.time}</span></div>)}
                    </Card>
                  </TabsContent>

                  <TabsContent value="documents" className="mt-4">
                    <Card className="p-5"><h3 className="font-bold mb-4">Documents</h3>
                      {(cur.documents || []).length === 0 ? <p className="text-sm text-muted-foreground">No documents on file.</p> : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">{cur.documents.map((d, i) => (
                          <div key={i} className="flex items-center justify-between p-2 rounded-lg border border-border text-sm">
                            <span className="flex items-center gap-2">{d.status === "complete" ? <CheckCircle2 size={15} className="text-emerald-600" /> : <XCircle size={15} className="text-rose-500" />}{d.type}</span>
                            <StatusBadge status={d.status} />
                          </div>
                        ))}</div>
                      )}
                    </Card>
                  </TabsContent>

                  <TabsContent value="profile" className="mt-4">
                    <Card className="p-5"><h3 className="font-bold mb-4">Player Profile</h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                        {[["Player ID", cur.player.player_code], ["Date of Birth", cur.player.dob], ["Age", cur.player.age], ["Category", cur.player.category], ["Position", cur.player.position || "—"], ["Preferred Foot", cur.player.preferred_foot || "—"], ["City", cur.player.city || "—"], ["Team", cur.team?.name || "—"], ["Status", cur.player.status]].map(([k, v]) => (
                          <div key={k}><div className="text-xs text-muted-foreground">{k}</div><div className="font-medium">{v}</div></div>
                        ))}
                      </div>
                    </Card>
                  </TabsContent>
                </Tabs>
              </>
            )}
          </>
        )}

        <Card className="p-5 mt-6">
          <h3 className="font-bold mb-4 flex items-center gap-2"><Bell size={16} /> Notifications {unread > 0 && <span className="text-xs bg-emerald-600 text-white rounded-full px-2 py-0.5">{unread} new</span>}</h3>
          {notifs.length === 0 ? <p className="text-sm text-muted-foreground">No notifications yet.</p> : (
            <div className="space-y-2" data-testid="parent-notifications">{notifs.map((n) => (
              <div key={n.id} className={`flex items-start justify-between gap-3 p-3 rounded-lg border ${n.read ? "border-border" : "border-emerald-500/40 bg-emerald-500/5"}`}>
                <div><div className="text-sm font-medium">{n.title}</div><div className="text-xs text-muted-foreground mt-0.5">{new Date(n.created_at).toLocaleString()}</div></div>
                {!n.read && <Button size="sm" variant="ghost" onClick={() => markRead(n.id)}>Mark read</Button>}
              </div>
            ))}</div>
          )}
        </Card>
      </main>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Change Password</DialogTitle></DialogHeader>
          {data?.parent?.must_change_password && <p className="text-sm text-amber-600 bg-amber-500/10 rounded-lg px-3 py-2">For your security, please set a new password.</p>}
          <div className="space-y-3">
            <div><Label className="mb-1.5 block">Current / Temporary Password</Label><Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="pw-current" /></div>
            <div><Label className="mb-1.5 block">New Password</Label><Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="pw-new" /></div>
            <div><Label className="mb-1.5 block">Confirm New Password</Label><Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="pw-confirm" /></div>
          </div>
          <DialogFooter><Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={changePw} data-testid="pw-save">Change Password</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
